import ProgressButton from '../../../../../components/ProgressButton';
import Slider from 'rc-slider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  faMinus,
  faPlus,
  faQuestion
} from '@fortawesome/pro-solid-svg-icons';
import DropDown from '../../../../../components/DropDown';
import CustomToast from '../../../../../components/Toast';
import usePrintPdfGenerator from '../../../../../utils/PrintPdfGenerator';
import PageSize from '../../../../../enums/pageSize';
import PropTypes from 'prop-types';
import SkeletonGrid from '../../../../../components/SkeletonLoader/SkeletonGrid';
import { useEffect } from 'react';
import { getSetType } from '../../../../myContent/utils/getSetType';
import { useLocation } from 'react-router';
import { getAudioPlayerImage } from '../utils/GetAudioPlayerImage';
import ActivityTypes from '../../../../../enums/activityTypes';

const PrintOptions = ({
  assignmentName,
  setName,
  displayTopValue,
  setDisplayTopValue,
  setFontStyleClass,
  printOptions,
  isLoading,
  updateSettings,
  resetSettings,
  activityType
}) => {
  const minFontSize = 10;
  const maxFontSize = 24;
  const location = useLocation();
  const activeTab = location.pathname.toLowerCase().split('/mycontent/')[1]?.split('/')[0];

  const { printPDF, isPdfGenerating } = usePrintPdfGenerator();

  const fontStyles = [
    {
      id: 'Times New Roman',
      name: 'Default',
      class: 'times'
    },
    {
      id: 'Arial',
      name: 'Arial',
      class: 'arial'
    },
    {
      id: 'Comic Sans MS',
      name: 'Comic Sans',
      class: 'comic-sans'
    },
    {
      id: 'Courier',
      name: 'Courier',
      class: 'courier'
    },
    {
      id: 'Tahoma',
      name: 'Tahoma',
      class: 'tahoma'
    },
    {
      id: 'Times New Roman',
      name: 'Times New Roman',
      class: 'times'
    },
    {
      id: 'Verdana',
      name: 'Verdana',
      class: 'verdana'
    }
  ];

  const handleDisplayTopValue = (event) => {
    const displayData = event.target.value;
    setDisplayTopValue(displayData);
  };

  const handleFontStyle = (event) => {
    let fontStyle = event.target.value;

    switch (fontStyle) {
    case 'Times New Roman':
      setFontStyleClass('times');
      break;
    case 'Arial':
      setFontStyleClass('arial');
      break;
    case 'Comic Sans MS':
      setFontStyleClass('comic-sans');
      break;
    case 'Courier':
      setFontStyleClass('courier');
      break;
    case 'Tahoma':
      setFontStyleClass('tahoma');
      break;
    case 'Verdana':
      setFontStyleClass('verdana');
      break;
    default:
      fontStyle = 'Times New Roman';
      setFontStyleClass('times');
      break;
    }
    
    updateSettings({ fontStyle: fontStyle });
  };

  const updateFontStyleClass = (fontStyle) => {
    const font = fontStyles.find(x => x.id === fontStyle);
    setFontStyleClass(font.class);
  };

  useEffect(() => {
    if(printOptions?.fontStyle)
      updateFontStyleClass(printOptions.fontStyle);
  }, [printOptions]);

  const incrementFontSize = () => {
    if (printOptions?.fontSize < maxFontSize) {
      updateSettings({ fontSize: printOptions.fontSize + 2 });
    }
  };

  const decrementFontSize = () => {
    if (printOptions?.fontSize > minFontSize) {
      updateSettings({ fontSize: printOptions.fontSize - 2 });
    }
  };

  const onSliderChange = (value) => {
    updateSettings({imageCompressionPercent: value});
  };

  const handleNameLine = () => {
    updateSettings({ showNameLine: !printOptions.showNameLine });
  };

  const handleTeacherName = () => {
    updateSettings({ showTeacherName: !printOptions.showTeacherName });
  };

  const handleAnswerKey = () => {
    updateSettings({ showAnswerKey: !printOptions.showAnswerKey });
  };

  const handleAnswerSpace = () => {
    updateSettings({ showAnswerSpace: !printOptions.showAnswerSpace });
  };

  const handleDisplayAnswer = () => {
    updateSettings({ displayAnswers: !printOptions.displayAnswers });
  };

  const handleDefinitions = () => {
    updateSettings({ hideDefinition: !printOptions.hideDefinition });
  };

  const handleTwoColumns = () => {
    updateSettings({ twoColumn: !printOptions.twoColumn });
  };
  
  const checkboxConfigs = [
    {
      id: 'nameLine',
      label: 'Show name line',
      onChange: handleNameLine,
      checked: `${printOptions?.showNameLine ? 'checked' : ''}`,
      show: true,
    },
    {
      id: 'teacherName',
      label: 'Show teacher name',
      onChange: handleTeacherName,
      checked: `${printOptions?.showTeacherName ? 'checked' : ''}`,
      show: true,
    },
    {
      id: 'answerKey',
      label: 'Append Answer Key',
      onChange: handleAnswerKey,
      checked: `${printOptions?.showAnswerKey ? 'checked' : ''}`,
      disabled: !!printOptions?.displayAnswers,
      show: activeTab === 'questionsets',
    },
    {
      id: 'answerSpace',
      label: 'Leave Answer Space',
      onChange: handleAnswerSpace,
      checked: `${printOptions?.showAnswerSpace ? 'checked' : ''}`,
      disabled: !!printOptions?.displayAnswers,
      show: activeTab === 'questionsets',
    },
    {
      id: 'displayAnswers',
      label: 'Display Answers',
      onChange: handleDisplayAnswer,
      checked: `${printOptions?.displayAnswers ? 'checked' : ''}`,
      disabled: !!printOptions?.showAnswerKey ||  !!printOptions?.showAnswerSpace,
      show: activeTab === 'questionsets',
    },
    {
      id: 'hideDefinition',
      label: 'Hide definitions',
      onChange: handleDefinitions,
      checked: `${printOptions?.hideDefinition ? 'checked' : ''}`,
      show: activeTab !== 'questionsets',
    },
    {
      id: 'twoColumn',
      label: 'Two columns',
      onChange: handleTwoColumns,
      checked: `${printOptions?.twoColumn ? 'checked' : ''}`,
      show: true
    }
  ];

  const handleReset = () => {
    setFontStyleClass('times');
    setDisplayTopValue('');
    resetSettings();
  };
  
  const handlePrintPDF = async () => {
    if(isPdfGenerating) return;
  
    const contentWrapper = document.getElementById('vocabulary-data');
    const contentClone = contentWrapper.cloneNode(true);
    const buttons = contentClone.querySelector('#checkData');
    const datas = contentClone.querySelectorAll('.doNotPrint');
    const checkboxes = contentClone.querySelectorAll('.checkboxes');
    const itemsBorder = contentClone.querySelectorAll('.check-items-border');
    const itemsNoBorder = contentClone.querySelectorAll('.check-items');
    const resizableWrapper = contentWrapper.querySelectorAll('.resizable-img-wrapper');
    const cloneWrappers = contentClone.querySelectorAll('.resizable-img-wrapper');
    const stacks = contentClone.querySelectorAll('.stackBlock');
    const paddedContainer = contentClone.querySelector('.print-content');
    const audioElements = contentClone.querySelectorAll('audio');

    if (paddedContainer) {
      paddedContainer.style.paddingLeft = '0';
      paddedContainer.style.paddingRight = '0';
    }

    buttons.style.display = 'none';
    datas.forEach((element) => {
      element.classList.add('d-none');
    });

    stacks.forEach((stack, index) => {
      const questions = stack.querySelectorAll('.questionBlock');

      const allDoNotPrint = Array.from(questions).every(q =>
        q.classList.contains('doNotPrint')
      );

      if (allDoNotPrint) {
        stack.classList.add('d-none');
        if (index < stacks.length - 1) {
          const nextStack = stacks[index + 1];
          nextStack.classList.remove('page-break-before');
        }
      }
    });

    checkboxes.forEach((element) => {
      element.style.display = 'none';
    });
  
    itemsNoBorder.forEach((element) => {
      element.style.border = 'none';
      element.style.display = 'none';
    });

    audioElements.forEach(aud => {
      aud.outerHTML = getAudioPlayerImage();
    });

    cloneWrappers.forEach((cloneWrapper, index) => {
      const imgWrapper = resizableWrapper[index];
      if (!imgWrapper) return;

      cloneWrapper.style.border = 'none';
      cloneWrapper.style.outline = 'none';

      const img = cloneWrapper.querySelector('img');
      if (!img) return;

      const paddingWidth = 8;
      img.style.width = (imgWrapper.offsetWidth - paddingWidth) + 'px';
      img.style.height = 'auto';
      img.style.maxWidth = 'none';
    });
  
    itemsBorder.forEach((element) => {
      element.style.borderBottom = '1px dashed #E2E2E2';
    });
  
    if (itemsBorder.length <= 0) {
      CustomToast('Select atleast one.', 'info');
      return;
    }
  
    await printPDF('VocabularyPrint', contentClone.innerHTML, PageSize.A3);
  };

  return (
    <div className="vocabulary-settings no-select">
      {isLoading ?
        <>
          <SkeletonGrid
            rowCount={2}
            colCount={1}
            className='mt-2' />
          <SkeletonGrid
            rowCount={2}
            colCount={1}
            className='p-3 h-150 mt-3'
          />
          <SkeletonGrid
            rowCount={4}
            colCount={1}
            className='p-3 mt-3'
          />
          <SkeletonGrid
            rowCount={1}
            colCount={2}
            className='p-3 mt-3'
          />
        </>
        :
        <>
          <h5 className="mb-3 fw-500">Print Options</h5>
          {(assignmentName || setName) && (
            <div className="mb-2">
              <label className="form-label fs-14">{activityType == ActivityTypes.FlashCard ? 'Flashcard Name' : (assignmentName ? 'Assignment Name' : getSetType(activeTab) + ' Set Name')}</label>
              <input type="text" className="form-control" value={assignmentName || setName} disabled />
            </div>
          )}
          <div className="mb-2">
            <label className="form-label fs-14">
              {`Text to display at the top of the ${activeTab === 'questionsets' ? 'question' : 'vocabulary'} study sheet`}
            </label>
            <textarea
              className="form-control"
              value={displayTopValue}
              onChange={handleDisplayTopValue}></textarea>
          </div>
          <div className="row">
            <div className="col my-2">
              <DropDown
                label="Font"
                value={fontStyles}
                onChangeHandler={handleFontStyle}
                selectedValue={printOptions?.fontStyle}
              />
            </div>
            <div className="col my-2">
              <label className="form-label fs-14">Font Size</label>
              <div className="col">
                <div className="d-flex gap-2 align-items-center counter-wrapper">
                  <div className="btn counter-btn" onClick={decrementFontSize}>
                    <FontAwesomeIcon icon={faMinus} className="counter-icon" />
                  </div>
                  <div className="counter fs-14">{printOptions?.fontSize}</div>
                  <div className="btn counter-btn" onClick={incrementFontSize}>
                    <FontAwesomeIcon icon={faPlus} className="counter-icon" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="checkbox-wrapper">
            {checkboxConfigs.filter((opt) => opt.show).map((opt) => (
              <div key={opt.id}>
                {opt.id === 'twoColumn' && (
                  <div className="separator my-2"></div>
                )}
                <div className={`d-flex gap-2 ${opt.id === 'twoColumn' ? 'pt-2' : ''}`}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    onChange={opt.onChange}
                    checked={opt.checked}
                    disabled={opt.disabled}
                  />{' '}
                  <label className="form-label fs-14">
                    {opt.label}
                  </label>
                </div>
              </div>
            ))}
          </div>
          <div className="slider pb-5">
            <div className="d-flex row slider-label">
              <label className="form-label d-flex gap-2 col">
                <span className='fs-14'>Image compression</span>
                <OverlayTrigger
                  key="image-overlay"
                  placement="left"
                  overlay={
                    <Tooltip className="image-info-tooltip">
                      <div>
                        <p>
                        Selecting two-column printing can reduce the number of pages that are
                        printed. It may be necessary to shrink some images in order to fit them
                        into a column. This might make the image unreadable.
                        </p>
                        <p>
                        The compression image option allows you to specify how much images can
                        be compressed. Often, 20% is a good choice but depending on what is in
                        the graphic you may be able to use a higher percentage or need to use a
                        lower value. The page will not use columns if the images will not fit
                        using the specified compression value.
                        </p>
                      </div>
                    </Tooltip>
                  }>
                  <FontAwesomeIcon icon={faQuestion} className="question-icon" />
                </OverlayTrigger>
              </label>
              <input
                type="text"
                value={`${printOptions?.imageCompressionPercent}%`}
                className="form-control col flex-row-reverse no-select"
                readOnly
              />
            </div>
            <div className="sliding-bar">
              <Slider
                min={0}
                max={100}
                value={printOptions?.imageCompressionPercent}
                onChange={onSliderChange}
                marks={{ 0: '0%', 100: '100%' }}
              />
            </div>
          </div>
          <div className="d-flex gap-3 justify-content-between my-2">
            <button className="btn btn-width btn-castleGray" onClick={handleReset}>
              Reset
            </button>
            <ProgressButton
              isLoading={isPdfGenerating}
              onClick={handlePrintPDF}
              loadingLabel="Generating PDF"
              label="Print to PDF"
              className="btn btn-castleRed"
            />
          </div>
        </>
      }
    </div>
  );
};

PrintOptions.propTypes = {
  assignmentName: PropTypes.string,
  setName: PropTypes.string,
  displayTopValue: PropTypes.string,
  setDisplayTopValue: PropTypes.func,
  setFontStyleClass: PropTypes.func,
  printOptions: PropTypes.object,
  isLoading: PropTypes.bool,
  updateSettings: PropTypes.func,
  resetSettings: PropTypes.func,
  activityType: PropTypes.number
};

export default PrintOptions;